import { type FC, type ReactNode, useState, useEffect, useRef } from 'react'
import { Search, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

interface DataTableToolbarProps {
    searchPlaceholder?: string
    onSearch?: (value: string) => void
    onFilter?: () => void
    actions?: ReactNode
    /** If true, debounce before calling onSearch (for API-driven search). Default: false */
    debounceSearch?: boolean
}

export const DataTableToolbar: FC<DataTableToolbarProps> = ({
    searchPlaceholder = 'Search...',
    onSearch,
    onFilter,
    actions,
    debounceSearch = false,
}) => {
    const [inputValue, setInputValue] = useState('')
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (!debounceSearch) {
            onSearch?.(inputValue)
            return
        }
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
            onSearch?.(inputValue)
        }, 400)
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [inputValue, debounceSearch, onSearch])

    return (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            {/* Search */}
            <div className="relative w-full flex-1 sm:max-w-md md:max-w-lg">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder={searchPlaceholder}
                    className="pl-9"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                />
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                {/* Filter button */}
                {onFilter && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onFilter}
                        className="gap-2 rounded-full"
                    >
                        <SlidersHorizontal className="h-4 w-4" />
                        Filter
                    </Button>
                )}

                {actions}
            </div>
        </div>
    )
}
